import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

import {
  ButtonV1,
  HyperLink,
  Loader,
  PaginationAction,
  StatusChip,
  SvgIcon,
} from "components";
import { Filters } from "./components";
import ApproveModal from "./ApproveModal";
import HoverBlock from "./HoverBlock";

import { useToaster } from "hooks";
import { decimalRoundOff, downloadPdf, moneyFormat } from "utils";
import { getCall_v2, postCall_v2 } from "services";
import { DO_SHORTCLOSE_HEADER, DO_STATUS } from "../constant";
import { success, error } from "constants";
import { STATUS_CHIP } from "constants";

const tablesPerPage = 20;
const STATUS_ORDER = [
  DO_STATUS.requested,
  DO_STATUS.approved,
  DO_STATUS.rejected,
];

const DoShortClose = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState();
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [doShortClosureList, setDoShortClosureList] = useState({});
  const triggerToaster = useToaster();

  const handleModal = (data) => {
    setModalData(data);
    setModalOpen(!isModalOpen);
  };

  useEffect(() => {
    setIsLoading(true);
    void fetchRequestData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const fetchRequestData = async () => {
    try {
      setIsLoading(true);

      const { data } = await postCall_v2(
        `/joms/api/external/v2/plant-supply/fetch-do-short-closure-list`,
        {
          page,
          tablesPerPage,
          filterDto: {
            customerGstin: filters?.customerGstin || null,
            orderNumber: filters?.orderNumber || null,
            status: filters?.status || null,
            orderCategory: filters?.orderCategory || null,
          },
        },
      );

      if (data) {
        setDoShortClosureList(data);
      }
    } catch (err) {
      triggerToaster(err?.data?.errorMessage || "Something went wrong!", error);
      console.error("Error fetching shipments:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (status, data) => {
    const requestData = data ? data : modalData;

    const payload = requestData.map((item) => ({
      psIdentifier: item.psIdentifier,
      lineItemId: item.lineItemId,
      orderNumber: item?.orderDetails?.orderNumber,
      status,
    }));

    try {
      const res = await postCall_v2(
        `/joms/api/dispatch-order/external/v1/bulk-short-close-do-approve`,
        payload,
      );
      if (res?.data?.success) {
        setModalOpen(false);
        triggerToaster(
          "Do Short Close approval workflow initiated successfully",
          success,
        );
        await fetchRequestData();
      } else {
        triggerToaster("Error occurred, please try again", error);
      }
    } catch (err) {
      triggerToaster("Error occurred, please try again", error);
    }
  };

  const handlePageChange = useCallback(
    (_event, pageNumber) => setPage(pageNumber),
    [],
  );

  const handleFilterSubmit = (filterData) => {
    setPage(0);
    setFilters(filterData);
  };

  const doShortCloseDTOList = doShortClosureList?.doShortCloseApprovalDTOList;

  const handleDownloadFile = async (row) => {
    const fileName = "attachment";
    try {
      const { data } = await getCall_v2(
        `/oms/plant-supply/view/do/short-close/file-by-id?id=${row?.id}`,
      );

      if (data?.successFul) {
        downloadPdf(data?.signedUrl, fileName);
        triggerToaster(`Downloading ${fileName}`, success);
      } else {
        triggerToaster(
          data?.errorMessage || `Unable to download ${fileName}`,
          error,
        );
      }
    } catch {
      triggerToaster(`Unable to download ${fileName}`, error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  return (
    <Box maxWidth={"100vw"}>
      <Filters onFilterSubmit={handleFilterSubmit} />
      <>
        <TableContainer
          component={Paper}
          sx={{
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
            marginTop: "20px",
          }}
        >
          {isLoading ? (
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ width: "100%", height: 200 }}
            >
              <Loader sx={{ marginTop: 0 }} />
            </Stack>
          ) : !!doShortCloseDTOList?.length ? (
            doShortCloseDTOList.map((items, index) => {
              const groupedByStatus = items.reduce((acc, item) => {
                acc[item.status] = acc[item.status] || [];
                acc[item.status].push(item);
                return acc;
              }, {});
              return (
                <React.Fragment key={index}>
                  {STATUS_ORDER.map((status) => {
                    const currentTableItems = groupedByStatus[status];
                    if (!currentTableItems?.length) return null;

                    return (
                      <React.Fragment key={status}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              {DO_SHORTCLOSE_HEADER.map((ele) => (
                                <TableCell key={ele}>{ele}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {currentTableItems.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.psIdentifier}</TableCell>
                                <TableCell>
                                  <HyperLink
                                    title={item?.orderDetails?.orderNumber}
                                  />
                                </TableCell>
                                <TableCell>{item?.productInfo?.name}</TableCell>
                                <TableCell>
                                  {decimalRoundOff(
                                    item?.releasedSkuQuantity,
                                    3,
                                  )}
                                </TableCell>
                                <TableCell>
                                  {decimalRoundOff(
                                    item?.dispatchedSkuQuantity,
                                    3,
                                  )}
                                </TableCell>
                                <TableCell>
                                  {decimalRoundOff(
                                    item?.releasedSkuQuantity -
                                      item?.dispatchedSkuQuantity,
                                    3,
                                  )}
                                </TableCell>
                                <TableCell>
                                  {moneyFormat(item?.residualValue)}
                                </TableCell>
                                <TableCell>
                                  {moneyFormat(item?.releasedValue)}
                                </TableCell>
                                <TableCell>
                                  <Box
                                    style={{
                                      textAlign: "center",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => handleDownloadFile(item)}
                                  >
                                    {item?.remarks ? (
                                      <HoverBlock
                                        LabelChildren={
                                          <IconButton>
                                            <SvgIcon
                                              name={"chat-unread"}
                                              width={24}
                                              height={24}
                                            />
                                          </IconButton>
                                        }
                                        data={{
                                          Remarks: item?.remarks,
                                        }}
                                      />
                                    ) : (
                                      <IconButton>
                                        <SvgIcon
                                          name={"chat-read"}
                                          width={24}
                                          height={24}
                                        />
                                      </IconButton>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}

                            <TableRow>
                              <TableCell
                                colSpan={9}
                                sx={{ backgroundColor: "#eee" }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={1}
                                >
                                  {status === "Requested" ? (
                                    <>
                                      <ButtonV1
                                        title="Reject"
                                        variant="outlined"
                                        onClick={() =>
                                          handleStatusChange(
                                            DO_STATUS.rejected,
                                            currentTableItems,
                                          )
                                        }
                                      />
                                      <ButtonV1
                                        title="Approve"
                                        onClick={() =>
                                          handleModal(currentTableItems)
                                        }
                                      />
                                    </>
                                  ) : (
                                    <StatusChip
                                      label={
                                        status === DO_STATUS.approved
                                          ? "Approved"
                                          : "Rejected"
                                      }
                                      type={
                                        status === DO_STATUS.approved
                                          ? STATUS_CHIP.success
                                          : STATUS_CHIP.error
                                      }
                                      size="medium"
                                    />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>

                        <Box height={20} />
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={DO_SHORTCLOSE_HEADER.length}>
                <Typography textAlign="center">No data found</Typography>
              </TableCell>
            </TableRow>
          )}

          {!isLoading && !!doShortClosureList && (
            <TablePagination
              rowsPerPageOptions={[tablesPerPage]}
              rowsPerPage={tablesPerPage}
              page={page}
              count={doShortClosureList?.totalCount ?? 0} // total tables/orders
              onPageChange={handlePageChange}
              sx={{ display: "flex", flexDirection: "column-reverse" }}
              ActionsComponent={PaginationAction}
            />
          )}
        </TableContainer>
      </>
      <>
        <ApproveModal
          open={isModalOpen}
          handleModal={handleModal}
          modalData={modalData}
          handleStatusChange={handleStatusChange}
        />
      </>
    </Box>
  );
};

export default DoShortClose;
